import * as React from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { Users, TrendingUp, BookOpen, Calendar, Lock, Clock, Wallet, Banknote } from "lucide-react";
import {
  AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import { format, subMonths, startOfMonth, endOfMonth } from "date-fns";
import { vi } from "date-fns/locale";
import { PortalLayout } from "@/components/portal-layout";
import { StatCard } from "@/components/stat-card";
import { StaggerReveal, RevealItem } from "@/components/animations";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { supabase } from "@/integrations/supabase/client";
import { useDisplayInfo } from "@/lib/auth";
import { DemoSeedButton } from "@/components/demo-seed-button";
import { useEntitlements } from "@/hooks/use-entitlements";
import { Button } from "@/components/ui/button";
import { Sparkles } from "lucide-react";
import { PageGuide } from "@/components/page-guide";
import { getAdminWelcomeTopic } from "@/lib/help-content";


export const Route = createFileRoute("/admin/")({ component: AdminDashboard });

const SUBJECT_COLORS = ["#0F4C81", "#FB923C", "#4ADE80", "#A78BFA", "#F472B6", "#22D3EE", "#FACC15"];

type Stats = {
  students: number;
  teachers: number;
  attendanceRate: number;
  monthSessions: number;
  monthly: { m: string; v: number }[];
  subjects: { name: string; value: number; color: string }[];
  todaySessions: { time: string; endTime: string; name: string; teacher: string; room: string; color: string }[];
  newStudents: { name: string; date: string; initials: string }[];
  newStudentsCount: number;
  bannedAccounts: { id: string; name: string; email: string }[];
  studentDiff: number;
  activeClassCount: number;
  completedClassCount: number;
  pausedClassCount: number;
  teachersWithoutClass: number;
  tuitionCollected: number;
  tuitionTotal: number;
  salaryUnpaidCount: number;
  salaryUnpaidAmount: number;
};

const EMPTY: Stats = {
  students: 0, teachers: 0, attendanceRate: 0, monthSessions: 0,
  monthly: [], subjects: [], todaySessions: [], newStudents: [], newStudentsCount: 0, bannedAccounts: [],
  studentDiff: 0, activeClassCount: 0, completedClassCount: 0, pausedClassCount: 0, teachersWithoutClass: 0,
  tuitionCollected: 0, tuitionTotal: 0, salaryUnpaidCount: 0, salaryUnpaidAmount: 0,
};


function initials(name: string) {
  return name.split(" ").map(p => p[0]).slice(-2).join("").toUpperCase();
}

function AdminDashboard() {
  const display = useDisplayInfo();
  const ent = useEntitlements();
  const [stats, setStats] = React.useState<Stats>(EMPTY);
  const [loading, setLoading] = React.useState(true);
  const [refreshKey, setRefreshKey] = React.useState(0);

  React.useEffect(() => {
    if (typeof window === "undefined") return;
    const onRefresh = () => setRefreshKey((k) => k + 1);
    const onFocus = () => setRefreshKey((k) => k + 1);
    window.addEventListener("ezyclass:dashboard-refresh", onRefresh);
    window.addEventListener("focus", onFocus);
    return () => {
      window.removeEventListener("ezyclass:dashboard-refresh", onRefresh);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  React.useEffect(() => {
    if (!ent.dashboard) { setLoading(false); return; }
    (async () => {
      try {
        const today = new Date();
        const todayStart = new Date(today); todayStart.setHours(0,0,0,0);
        const todayEnd = new Date(today); todayEnd.setHours(23,59,59,999);
        const monthStart = startOfMonth(today);
        const monthEnd = endOfMonth(today);

        const curMonthKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;

        // Center-scoped via RLS on profiles + user_roles (both filter by center now)
        const [profilesRes2, rolesRes2] = await Promise.all([
          supabase
            .from("profiles")
            .select("id, display_name, email, created_at, is_banned")
            .order("created_at", { ascending: false })
            .limit(1000),
          supabase.from("user_roles").select("role, user_id"),
        ]);

        const allProfiles = profilesRes2.data ?? [];
        const profileIdSet = new Set(allProfiles.map(p => p.id));
        const rolesInCenter = (rolesRes2.data ?? []).filter(r => profileIdSet.has(r.user_id));
        const studentIds = new Set(rolesInCenter.filter(r => r.role === "student").map(r => r.user_id));
        const teacherIds = new Set(rolesInCenter.filter(r => r.role === "teacher").map(r => r.user_id));


        const [classesRes, attRes, sessionsRes, monthSessRes, feesRes, salariesRes] = await Promise.all([
          supabase.from("classes").select("id, name, subject, teacher_id, created_at, status"),
          supabase.from("attendances").select("status"),
          supabase.from("class_sessions")
            .select("id, starts_at, ends_at, room, class_id")
            .gte("starts_at", todayStart.toISOString())
            .lte("starts_at", todayEnd.toISOString())
            .order("starts_at"),
          supabase.from("class_sessions").select("id", { count: "exact", head: true })
            .gte("starts_at", monthStart.toISOString())
            .lte("starts_at", monthEnd.toISOString()),
          supabase.from("student_fees").select("total_fee, paid_amount, is_paid").eq("month", curMonthKey),
          supabase.from("teacher_salaries").select("salary_amount, is_paid").eq("month", curMonthKey),
        ]);

        const classes = classesRes.data ?? [];
        const att = attRes.data ?? [];
        const sessions = sessionsRes.data ?? [];
        const profiles = allProfiles.map(p => ({
          id: p.id,
          display_name: p.display_name ?? "",
          email: p.email ?? "",
          created_at: p.created_at,
        }));

        const presentCount = att.filter(a => a.status === "present" || a.status === "late").length;
        const attendanceRate = att.length ? Math.round((presentCount / att.length) * 100) : 0;

        const months: { m: string; v: number }[] = [];
        const studentProfiles = profiles.filter(p => studentIds.has(p.id));
        for (let i = 7; i >= 0; i--) {
          const start = startOfMonth(subMonths(today, i));
          const next = startOfMonth(subMonths(today, i - 1));
          const count = studentProfiles.filter(p => {
            const d = new Date(p.created_at);
            return d >= start && d < next;
          }).length;
          months.push({ m: format(start, "MMM", { locale: vi }), v: count });
        }
        let cum = Math.max(0, studentIds.size - months.reduce((a, b) => a + b.v, 0));
        const monthly = months.map(m => { cum += m.v; return { m: m.m, v: cum }; });

        const subjectMap = new Map<string, number>();
        for (const c of classes) {
          const key = c.subject || "Khác";
          subjectMap.set(key, (subjectMap.get(key) ?? 0) + 1);
        }
        const subjects = Array.from(subjectMap.entries()).map(([name, value], i) => ({
          name, value, color: SUBJECT_COLORS[i % SUBJECT_COLORS.length],
        }));

        const classMap = new Map(classes.map(c => [c.id, c]));
        const teacherMap = new Map(profiles.map(p => [p.id, p]));
        const todaySessions = sessions.slice(0, 6).map((s, i) => {
          const c = classMap.get(s.class_id);
          const t = c?.teacher_id ? teacherMap.get(c.teacher_id) : null;
          return {
            time: format(new Date(s.starts_at), "HH:mm"),
            endTime: s.ends_at ? format(new Date(s.ends_at), "HH:mm") : "",
            name: c?.name ?? "—",
            teacher: t?.display_name ?? t?.email?.split("@")[0] ?? "—",
            room: s.room ?? "—",
            color: SUBJECT_COLORS[i % SUBJECT_COLORS.length],
          };
        });

        // New students this month
        const newThisMonth = studentProfiles.filter(p => new Date(p.created_at) >= monthStart);
        const lastMonthStart = startOfMonth(subMonths(today, 1));
        const lastMonthEnd = endOfMonth(subMonths(today, 1));
        const lastMonthNewCount = studentProfiles.filter(p => {
          const d = new Date(p.created_at);
          return d >= lastMonthStart && d <= lastMonthEnd;
        }).length;
        const studentDiff = newThisMonth.length - lastMonthNewCount;

        const newStudents = newThisMonth.slice(0, 5).map(p => {
          const name = p.display_name || p.email.split("@")[0];
          const diff = today.getTime() - new Date(p.created_at).getTime();
          const days = Math.floor(diff / 86400000);
          const date = days === 0 ? "Hôm nay" : days === 1 ? "Hôm qua" : `${days} ngày trước`;
          return { name, date, initials: initials(name) };
        });

        const bannedAccounts = allProfiles
          .filter(p => p.is_banned)
          .slice(0, 5)
          .map(b => ({
            id: b.id,
            name: b.display_name || b.email?.split("@")[0] || "—",
            email: b.email || "",
          }));


        const activeClassCount = classes.filter(c => (c.status ?? "active") === "active").length;
        const completedClassCount = classes.filter(c => c.status === "completed").length;
        const pausedClassCount = classes.filter(c => c.status === "paused").length;

        const teachersWithClass = new Set(classes.filter(c => c.teacher_id).map(c => c.teacher_id!));
        const teachersWithoutClass = [...teacherIds].filter(id => !teachersWithClass.has(id)).length;

        const fees = feesRes.data ?? [];
        const salaries = salariesRes.data ?? [];
        const tuitionCollected = fees.reduce((a, f) => a + Number(f.paid_amount ?? 0), 0);
        const tuitionTotal = fees.reduce((a, f) => a + Number(f.total_fee ?? 0), 0);
        const unpaidSalaries = salaries.filter((s) => !s.is_paid);
        const salaryUnpaidCount = unpaidSalaries.length;
        const salaryUnpaidAmount = unpaidSalaries.reduce((a, s) => a + Number(s.salary_amount ?? 0), 0);

        setStats({
          students: studentIds.size,
          teachers: teacherIds.size,
          attendanceRate,
          monthSessions: monthSessRes.count ?? 0,
          monthly, subjects, todaySessions,
          newStudents,
          newStudentsCount: newThisMonth.length,
          bannedAccounts,
          studentDiff,
          activeClassCount, completedClassCount, pausedClassCount,
          teachersWithoutClass,
          tuitionCollected, tuitionTotal, salaryUnpaidCount, salaryUnpaidAmount,
        });

      } finally {
        setLoading(false);
      }
    })();
  }, [ent.dashboard, refreshKey]);

  const firstName = (display.name || "Quản trị").split(" ").slice(-1)[0];

  if (!ent.dashboard && !ent.isLoading) {
    return (
      <PortalLayout
        portal="admin"
        title={`Chào, ${firstName} 👋`}
        breadcrumb={[{ label: "Admin" }, { label: "Tổng quan" }]}
      >
        <PageGuide topic={getAdminWelcomeTopic(ent.maxTeachers === 0)} storageKey="admin-welcome" />
        <div className="mb-6 rounded-2xl bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 p-5 flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="size-10 rounded-lg bg-blue-600 text-white grid place-items-center shrink-0">
              <Sparkles className="size-5" />
            </div>
            <div>
              <h3 className="font-display font-semibold text-slate-900 dark:text-white">
                Gói {ent.tier === "free" ? "Miễn phí" : "Gia sư"}
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                Nâng cấp gói Trung tâm để xem Dashboard đầy đủ, báo cáo, quản lý tài chính, thông báo hàng loạt.
              </p>
            </div>
          </div>
          <Link to="/billing">
            <Button size="sm">Nâng cấp</Button>
          </Link>
        </div>

        <div className="grid sm:grid-cols-2 gap-4 mb-6">
          <div className="bg-card border rounded-2xl p-5">
            <div className="text-xs uppercase text-muted-foreground tracking-wider">Lớp học</div>
            <div className="font-display text-3xl font-semibold mt-1">
              {ent.currentClasses} <span className="text-base text-muted-foreground font-normal">/ {ent.maxClasses === -1 ? "∞" : ent.maxClasses}</span>
            </div>
          </div>
          <div className="bg-card border rounded-2xl p-5">
            <div className="text-xs uppercase text-muted-foreground tracking-wider">Học viên</div>
            <div className="font-display text-3xl font-semibold mt-1">
              {ent.currentStudents} <span className="text-base text-muted-foreground font-normal">/ {ent.maxStudents === -1 ? "∞" : ent.maxStudents}</span>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <Link to="/admin/classes" className="bg-card border rounded-2xl p-5 hover:shadow-md transition">
            <BookOpen className="size-5 mb-2" style={{ color: "var(--portal)" }} />
            <div className="font-display font-semibold">Quản lý lớp học</div>
            <p className="text-sm text-muted-foreground mt-1">Tạo lớp, lên lịch, điểm danh</p>
          </Link>
          <Link to="/admin/students" className="bg-card border rounded-2xl p-5 hover:shadow-md transition">
            <Users className="size-5 mb-2" style={{ color: "var(--portal)" }} />
            <div className="font-display font-semibold">Quản lý học viên</div>
            <p className="text-sm text-muted-foreground mt-1">Thêm và xem danh sách học viên</p>
          </Link>
        </div>
      </PortalLayout>
    );
  }

  return (
    <PortalLayout
      portal="admin"
      title={`Chào, ${firstName} 👋`}
      breadcrumb={[{ label: "Admin" }, { label: "Tổng quan" }]}
    >
      <PageGuide topic={getAdminWelcomeTopic(ent.maxTeachers === 0)} storageKey="admin-welcome" />
      <div className="-mt-3 mb-8 flex items-center justify-between gap-4 flex-wrap">
        <p className="text-muted-foreground">Đây là toàn cảnh trung tâm của bạn hôm nay.</p>
        <DemoSeedButton />
      </div>



      <StaggerReveal className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8" stagger={0.08}>
        <RevealItem>
          <StatCard
            label="HS mới tháng này"
            value={stats.newStudentsCount}
            icon={<Users className="size-5" />}
            trend={stats.studentDiff !== 0 ? { value: String(Math.abs(stats.studentDiff)), up: stats.studentDiff > 0 } : undefined}
            accent
          />
        </RevealItem>
        <RevealItem>
          <StatCard
            label="Lớp đang hoạt động"
            value={stats.activeClassCount}
            suffix={stats.completedClassCount ? ` · ${stats.completedClassCount} hoàn tất` : ""}
            icon={<BookOpen className="size-5" />}
          />
        </RevealItem>
        <RevealItem><StatCard label="Tỉ lệ điểm danh" value={stats.attendanceRate} suffix="%" icon={<TrendingUp className="size-5" />} /></RevealItem>
        <RevealItem><StatCard label="Buổi học tháng này" value={stats.monthSessions} icon={<Calendar className="size-5" />} /></RevealItem>
      </StaggerReveal>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        <div className="bg-card border rounded-2xl p-5 shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <Wallet className="size-4" style={{ color: "var(--portal)" }} />
            <h3 className="font-display font-semibold">Thu học phí tháng này</h3>
          </div>
          <div className="text-2xl font-bold">
            {new Intl.NumberFormat("vi-VN").format(Math.round(stats.tuitionCollected))}đ
            <span className="text-sm font-normal text-muted-foreground"> / {new Intl.NumberFormat("vi-VN").format(Math.round(stats.tuitionTotal))}đ</span>
          </div>
          <div className="mt-3 h-2 rounded-full bg-muted overflow-hidden">
            <div className="h-full rounded-full transition-all"
              style={{
                width: `${stats.tuitionTotal ? Math.min(100, (stats.tuitionCollected / stats.tuitionTotal) * 100) : 0}%`,
                background: "var(--portal)",
              }} />
          </div>
          <p className="text-xs text-muted-foreground mt-1.5">
            {stats.tuitionTotal ? Math.round((stats.tuitionCollected / stats.tuitionTotal) * 100) : 0}% đã thu
          </p>
        </div>

        <div className="bg-card border rounded-2xl p-5 shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <Banknote className="size-4" style={{ color: "var(--portal)" }} />
            <h3 className="font-display font-semibold">Lương GV tháng này</h3>
          </div>
          <div className="text-2xl font-bold">
            {new Intl.NumberFormat("vi-VN").format(Math.round(stats.salaryUnpaidAmount))}đ
          </div>
          <div className="mt-2">
            {stats.salaryUnpaidCount > 0 ? (
              <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded-full bg-amber-100 text-amber-700 dark:text-amber-300">
                {stats.salaryUnpaidCount} GV chưa được trả
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 dark:text-emerald-300">
                ✓ Đã trả đủ
              </span>
            )}
          </div>
        </div>
      </div>




      <div className="grid lg:grid-cols-5 gap-5 mb-8">
        <motion.div
          initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 }}
          className="lg:col-span-3 bg-card border rounded-2xl p-6 shadow-sm"
        >
          <div className="flex items-baseline justify-between mb-5">
            <div>
              <h3 className="font-display text-lg font-semibold">Học sinh theo tháng</h3>
              <p className="text-xs text-muted-foreground mt-0.5">Tổng tích lũy 8 tháng gần nhất</p>
            </div>
          </div>
          <div className="h-64">
            <ResponsiveContainer>
              <AreaChart data={stats.monthly}>
                <defs>
                  <linearGradient id="gradStudents" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#0F4C81" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#0F4C81" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="oklch(0.92 0.01 255)" vertical={false} />
                <XAxis dataKey="m" axisLine={false} tickLine={false} fontSize={11} />
                <YAxis axisLine={false} tickLine={false} fontSize={11} allowDecimals={false} />
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid var(--border)" }} />
                <Area type="monotone" dataKey="v" stroke="#0F4C81" strokeWidth={2.5} fill="url(#gradStudents)" animationDuration={1200} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.4 }}
          className="lg:col-span-2 bg-card border rounded-2xl p-6 shadow-sm"
        >
          <h3 className="font-display text-lg font-semibold mb-1">Phân bổ môn học</h3>
          <p className="text-xs text-muted-foreground mb-3">Theo số lớp</p>
          <div className="h-64">
            {stats.subjects.length ? (
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={stats.subjects} dataKey="value" innerRadius={55} outerRadius={85} paddingAngle={2} animationDuration={1200}>
                    {stats.subjects.map((s, i) => <Cell key={i} fill={s.color} />)}
                  </Pie>
                  <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full grid place-items-center text-sm text-muted-foreground">Chưa có lớp học</div>
            )}
          </div>
        </motion.div>
      </div>

      <div className="grid md:grid-cols-3 gap-5">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
          className="bg-card border rounded-2xl p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <Calendar className="size-4" style={{ color: "var(--portal)" }} />
            <h3 className="font-display font-semibold">Lớp học hôm nay</h3>
          </div>
          {stats.todaySessions.length ? (
            <ul className="space-y-3">
              {stats.todaySessions.map((c, i) => (
                <li key={i} className="flex items-center gap-3 group">
                  <div className="text-xs font-medium text-muted-foreground whitespace-nowrap">
                    {c.time}{c.endTime ? ` – ${c.endTime}` : ""}
                  </div>
                  <div className="size-2 rounded-full shrink-0" style={{ background: c.color }} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{c.name}</div>
                    <div className="text-xs text-muted-foreground flex items-center gap-1.5">
                      <span className="truncate">{c.teacher}</span>
                      <span className="bg-muted px-1.5 py-0.5 rounded text-[10px] shrink-0">P.{c.room}</span>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">Không có lớp nào hôm nay.</p>
          )}
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55 }}
          className="bg-card border rounded-2xl p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <Users className="size-4" style={{ color: "var(--portal)" }} />
            <h3 className="font-display font-semibold">Học sinh mới tháng này ({stats.newStudentsCount})</h3>
          </div>
          {stats.newStudents.length ? (
            <ul className="space-y-3">
              {stats.newStudents.map((s, i) => (
                <li key={i} className="flex items-center gap-3">
                  <Avatar className="size-9">
                    <AvatarFallback style={{ background: "var(--portal-soft)", color: "var(--portal)" }} className="text-xs font-medium">{s.initials}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{s.name}</div>
                    <div className="text-xs text-muted-foreground flex items-center gap-1"><Clock className="size-3" />{s.date}</div>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">Chưa có học sinh mới tháng này.</p>
          )}
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}
          className="bg-card border rounded-2xl p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <Lock className="size-4" style={{ color: "var(--portal)" }} />
            <h3 className="font-display font-semibold">Tài khoản bị khóa</h3>
          </div>
          <div className="flex gap-3 mb-3 text-xs flex-wrap">
            <div className="flex items-center gap-1.5">
              <span className="size-2 rounded-full bg-amber-500" />
              <span className="text-muted-foreground">{stats.pausedClassCount} lớp tạm nghỉ</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="size-2 rounded-full bg-slate-400" />
              <span className="text-muted-foreground">{stats.teachersWithoutClass} GV chưa có lớp</span>
            </div>
          </div>

          {loading ? (
            <p className="text-sm text-muted-foreground">Đang tải...</p>
          ) : stats.bannedAccounts.length === 0 ? (
            <p className="text-sm text-emerald-600 font-medium">Không có tài khoản nào bị khóa ✓</p>
          ) : (
            <ul className="space-y-2.5">
              {stats.bannedAccounts.map((b) => (
                <li key={b.id} className="py-1.5 px-3 rounded-lg hover:bg-muted/50 transition-colors">
                  <Link to="/admin/students" className="block">
                    <div className="text-sm font-medium truncate">{b.name}</div>
                    <div className="text-xs text-muted-foreground truncate">{b.email}</div>
                  </Link>
                </li>
              ))}
              <li className="pt-1">
                <Link to="/admin/students" className="text-xs text-muted-foreground hover:text-foreground">Xem tất cả →</Link>
              </li>
            </ul>
          )}
        </motion.div>
      </div>
    </PortalLayout>
  );
}
